import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Frame } from 'lucide-react';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/PageHeader';
import { ProductVisualEditor } from '@/components/domain/ProductVisualEditor';
import { CATEGORIES, VIEW_KEYS, type ProductConfig } from '@/lib/product-config';

type Payload = { products: ProductConfig[] };

// Multi-Logo Designs — the catalog-wide visual placement surface (native
// replacement for product-config-admin's designs/zones tooling). Every card
// opens the zone editor directly.
export default function MultiLogo() {
  const { data, isLoading } = useSWR<Payload>('/api/products?limit=500', get);
  const [q, setQ] = useState('');
  const [fCat, setFCat] = useState('all');
  const [fState, setFState] = useState<'all' | 'placed' | 'unplaced'>('all');
  const [editing, setEditing] = useState<ProductConfig | null>(null);

  const products = useMemo(() => {
    return (data?.products || []).filter((p) => {
      if (fCat !== 'all' && p.category !== fCat) return false;
      const zoneCount = VIEW_KEYS.reduce((n, v) => n + (p.views?.[v]?.areas?.length || 0), 0);
      if (fState === 'placed' && zoneCount === 0) return false;
      if (fState === 'unplaced' && zoneCount > 0) return false;
      if (q.trim()) {
        const hay = `${p.displayName || ''} ${p.productTitle || ''} ${p.baseProduct?.styleNumber || ''} ${p.id}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, q, fCat, fState]);

  const placedTotal = (data?.products || []).filter((p) =>
    VIEW_KEYS.some((v) => (p.views?.[v]?.areas?.length || 0) > 0)
  ).length;

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Multi-Logo Designs"
        subtitle={`Draw print-area zones on garment views — ${placedTotal} of ${data?.products.length ?? 0} products have placements`}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select className="h-8 rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px]" value={fCat} onChange={(e) => setFCat(e.target.value)}>
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex gap-1.5">
          {([['all', 'All'], ['placed', 'Placed'], ['unplaced', 'Needs zones']] as const).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setFState(v)}
              className={`rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${fState === v ? 'bg-text text-white' : 'border border-black/[0.08] bg-surface text-muted hover:bg-bg'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, style #…" className="max-w-[260px] flex-1" />
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-[210px] rounded-[12px]" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-border p-12 text-center text-[12px] text-muted">
          No products match.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((p) => {
            const front = p.views?.front;
            const img = front?.imageUrl || front?.imageDataUrl;
            const counts = VIEW_KEYS.map((v) => ({ v, n: p.views?.[v]?.areas?.length || 0 })).filter(({ n }) => n > 0);
            return (
              <button
                key={p.id}
                onClick={() => setEditing(p)}
                className="group overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface text-left shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-raised"
              >
                <div className="flex h-[140px] items-center justify-center overflow-hidden bg-[#fafafa]">
                  {img ? (
                    <img src={img} alt={p.displayName || p.id} className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.04]" loading="lazy" />
                  ) : (
                    <Frame className="h-7 w-7 text-subtle/60" aria-hidden />
                  )}
                </div>
                <div className="p-3">
                  <div className="truncate text-[12px] font-medium">{p.displayName || p.productTitle || p.id}</div>
                  <div className="mt-0.5 truncate font-mono text-[10px] text-subtle">
                    {p.baseProduct?.styleNumber}{p.baseProduct?.color ? ` · ${p.baseProduct.color}` : ''}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {counts.length === 0 ? (
                      <Badge className="bg-warning/10 text-warning">no zones</Badge>
                    ) : (
                      counts.map(({ v, n }) => (
                        <Badge key={v} className="bg-primary/10 text-primary">{v} · {n}</Badge>
                      ))
                    )}
                    {(p.printAreas || []).length > 0 && (
                      <Badge className="bg-bg text-muted">{(p.printAreas || []).length} areas</Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <ProductVisualEditor
        open={!!editing}
        onClose={() => setEditing(null)}
        product={editing}
      />
    </div>
  );
}
